// @ts-strict-ignore
import { useEffect, useMemo } from "react";

import type {
  DashboardApiEndpointDefinitions,
  dashboardApiEndpoints,
  GetUseQueryOptions,
} from "@dashboardApi/apis";
import type { Developer } from "@dashboardApi/apis/developer";

import type { Permission } from "@lib/permissionConstants";

import { useBrazeDashboardConfig } from "@services/brazeDashboardConfig";
import { useDashboardApiQuery } from "@services/useDashboardApi";
import { useCurrentDeveloperContext } from "@services/useDeveloper/CurrentDeveloperContext";
import { useFeatureOn } from "@services/useFeatureOn";
import { useDeveloperViewModel } from "@services/viewModel/useDeveloperViewModel";
import { useViewModelClasses } from "@services/viewModel/useViewModelClasses";

import { createPermissionsMap } from "./createPermissionsMap";
import type { DeveloperPermissionInfo, DeveloperWithPermissionMap } from "./types";

/**
 * Retrieve the current developer's information.
 *
 * This is guaranteed to be available immediately, because the placeholder data is the current developer as
 * populated in Braze's dashboard config.
 *
 * As endpoints are added that update the developer, they can invalidate the cache, guaranteeing the developer is
 * only ever a few seconds out of date.
 *
 * In addition, this is pushed out to the meta-view model to keep legacy knockout views in sync
 */
// eslint-disable-next-line local-eslint-rules/enforce-api-query-hook-naming-convention
const useDeveloperLegacy = (
  options?: Omit<
    GetUseQueryOptions<typeof dashboardApiEndpoints, "GET", "/companies/:company_id/developers/current">,
    "placeholderData" | "keepPreviousData" | "select"
  >
) => {
  const user = useBrazeDashboardConfig("user");
  const companyId = user.company._id;
  const { DeveloperViewModel } = useViewModelClasses();
  const [developer, setDeveloper] = useDeveloperViewModel();
  const userDeveloperWithPermissionMap: DeveloperWithPermissionMap = useMemo(() => {
    return {
      ...user.developer,
      permissionMap: options.enabled
        ? createPermissionsMap(user.developer.permissions)
        : new Map<Permission, DeveloperPermissionInfo>(),
    };
  }, [user.developer, options.enabled]);
  // eslint-disable-next-line local-eslint-rules/enforce-dashboard-api-query-return
  const info = useDashboardApiQuery<
    "/companies/:company_id/developers/current",
    DashboardApiEndpointDefinitions["/companies/:company_id/developers/current"]["GET"],
    Developer,
    DeveloperWithPermissionMap
  >("/companies/:company_id/developers/current", {
    params: [companyId],
    options: {
      ...options,
      placeholderData: user.developer,
      // when app group ID changes, don't clear the `data` field, since developer does not vary from app group to app group
      keepPreviousData: true,
      notifyOnChangeProps: ["data"],
      select: (data) => {
        return options?.enabled
          ? {
              ...data,
              permissionMap: createPermissionsMap(data.permissions),
            }
          : userDeveloperWithPermissionMap;
      },
    },
  });

  useEffect(() => {
    if (!info.data) {
      return;
    }
    const data = { ...info.data };
    delete data.permissionMap;
    if (DeveloperViewModel && JSON.stringify(data) !== JSON.stringify(user.developer)) {
      // update the dashboard config
      user.developer = data;
      // update the view model
      const newDev = DeveloperViewModel.fromAjaxData(data);
      newDev.allowedTerritoriesByAppGroupIdByPermission(developer.allowedTerritoriesByAppGroupIdByPermission());
      setDeveloper(newDev);
    }
  }, [DeveloperViewModel, developer, info.data, setDeveloper, user]);

  return {
    ...info,
    // ensure that on error, we fall back to the dashboard config's developer instance
    data: info.data || userDeveloperWithPermissionMap,
  };
};

export const useDeveloper = (
  options?: Omit<
    GetUseQueryOptions<typeof dashboardApiEndpoints, "GET", "/companies/:company_id/developers/current">,
    "placeholderData" | "keepPreviousData" | "select"
  >
) => {
  const featureOn = useFeatureOn();
  const usingContext = featureOn("current_developer_context");

  const developerContext = useCurrentDeveloperContext({ enabled: usingContext });
  const developerLegacy = useDeveloperLegacy({ ...options, enabled: !usingContext });

  return usingContext ? developerContext : developerLegacy;
};
